import { sheetsEndpoint } from '../data/integrations';
import { images } from '../data/images';
import { normalizeImageList } from './imageUrl';
import { Availability, BodyType, Brand, FinanceOffer, Segment, Vehicle } from '../types/catalog';
import { SheetCatalog, SheetFinancing } from '../types/sheets';

const brandAliases: Record<string, string> = { vw: 'volkswagen', chevy: 'chevrolet' };

const bodyMap: Record<string, BodyType> = {
  suv: 'SUV',
  sedan: 'Sedán',
  hatchback: 'Hatchback',
  'pick-up': 'Pick-up',
  pickup: 'Pick-up',
  utilitario: 'Utilitario',
};

const segmentMap: Record<string, Segment> = { compacto: 'Compacto', mediano: 'Mediano', grande: 'Grande' };

const availabilityMap: Record<string, Availability> = {
  'entrega-inmediata-confirmada': 'Entrega inmediata',
  'entrega-inmediata': 'Entrega inmediata',
  disponible: 'Disponible',
  'a-pedido': 'A pedido',
  'sin-stock': 'Sin stock',
};

/** Normaliza un texto a identificador: minúsculas, sin acentos, con guiones. */
function key(s: string): string {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function brandSlug(name: string): string {
  const k = key(name);
  return brandAliases[k] ?? k;
}

const isScope = (f: SheetFinancing, scope: 'MARCA' | 'UNIDAD') => String(f.alcance).trim().toUpperCase() === scope;

function emptyOffer(): FinanceOffer {
  return {
    tna: null,
    cft: null,
    amortization: 'frances',
    terms: [],
    minDownPct: 0,
    maxFinanceAmount: null,
    monthlyInsurance: null,
    adminFees: null,
    taxNote: '',
    lender: 'Entidad a confirmar',
    promo: null,
    validUntil: '',
    conditions: 'Condiciones a confirmar con un asesor.',
    verified: false,
    updatedAt: new Date().toISOString(),
  };
}

function toOffer(f: SheetFinancing): FinanceOffer {
  return {
    tna: f.tna,
    cft: f.cft,
    amortization: String(f.sistema).trim().toUpperCase() === 'ALEMAN' ? 'aleman' : 'frances',
    terms: [...(f.plazos ?? [])].filter((n) => n > 0).sort((a, b) => a - b),
    minDownPct: f.anticipo_min_pct ?? 0,
    maxFinanceAmount: f.capital_max,
    monthlyInsurance: f.seguro_mensual,
    adminFees: f.gastos,
    taxNote: f.impuestos || '',
    lender: f.entidad || 'Entidad a confirmar',
    promo: f.promocion || null,
    validUntil: f.vigencia_hasta || '',
    conditions: f.condiciones || '',
    verified: Boolean(f.verificada),
    updatedAt: f.fecha_actualizacion || new Date().toISOString(),
  };
}

/** Lee el catálogo publicado desde la planilla del 0 KM. */
export async function fetchSheetCatalog(signal?: AbortSignal): Promise<SheetCatalog> {
  const res = await fetch(`${sheetsEndpoint}?action=okm.catalog`, { signal, redirect: 'follow' });
  if (!res.ok) throw new Error(`La planilla respondió con error ${res.status}`);
  const json = (await res.json()) as { ok: boolean; data?: Partial<SheetCatalog>; error?: string };
  if (!json.ok || !json.data) throw new Error(json.error || 'Respuesta inválida de la planilla');
  return {
    vehicles: Array.isArray(json.data.vehicles) ? json.data.vehicles : [],
    financing: Array.isArray(json.data.financing) ? json.data.financing : [],
    generado: json.data.generado ?? new Date().toISOString(),
  };
}

/**
 * Convierte la planilla al modelo de la landing.
 * - Filas de FINANCIACION_0KM con alcance MARCA reemplazan la condición general de esa marca.
 * - Una unidad toma condición propia si su financiacion_id apunta a una fila UNIDAD
 *   (o si hay una fila UNIDAD con su vehiculo_id); si no, aplica la de su marca.
 * - Marcas que no existan en la landing se crean automáticamente.
 * - Se descartan filas sin marca, modelo o precio válido.
 */
export function mergeSheetCatalog(
  raw: SheetCatalog,
  baseBrands: Brand[],
): { brands: Brand[]; vehicles: Vehicle[]; photoOnly: Record<string, string[]> } {
  const brands: Brand[] = baseBrands.map((b) => ({ ...b }));

  const ensureBrand = (name: string): Brand => {
    const id = brandSlug(name);
    let brand = brands.find((b) => b.id === id || brandSlug(b.name) === id);
    if (!brand) {
      const label = String(name).trim();
      brand = { id, name: label, slug: id, tagline: `Descubrí los 0 km ${label}.`, defaultOffer: emptyOffer() };
      brands.push(brand);
    }
    return brand;
  };

  raw.financing.filter((f) => isScope(f, 'MARCA') && f.marca).forEach((f) => {
    ensureBrand(f.marca).defaultOffer = toOffer(f);
  });

  const financingById = new Map(raw.financing.map((f) => [String(f.id), f]));

  const vehicles = raw.vehicles
    .filter((v) => v.id && v.marca && v.modelo && typeof v.precio_contado === 'number' && v.precio_contado > 0)
    .map((v): Vehicle => {
      const brand = ensureBrand(v.marca);
      const linked = v.financiacion_id ? financingById.get(String(v.financiacion_id)) : undefined;
      const unitRow =
        linked && isScope(linked, 'UNIDAD')
          ? linked
          : raw.financing.find((f) => isScope(f, 'UNIDAD') && String(f.vehiculo_id) === String(v.id));

      return {
        id: String(v.id),
        brandId: brand.id,
        model: String(v.modelo),
        version: String(v.version || ''),
        year: v.anio ?? new Date().getFullYear(),
        bodyType: bodyMap[key(v.carroceria)] ?? 'SUV',
        segment: segmentMap[key(v.segmento)] ?? 'Mediano',
        // Vacío = sin fotos en la planilla; el store completa con las del ejemplo del mismo id o una genérica.
        images: normalizeImageList(v.fotos ?? []),
        price: v.precio_contado as number,
        availability: availabilityMap[key(v.disponibilidad)] ?? 'A pedido',
        engine: v.motor,
        transmission: v.transmision,
        consumption: v.consumo,
        safety: v.seguridad ?? [],
        equipment: v.equipamiento ?? [],
        warranty: v.garantia,
        offer: unitRow ? toOffer(unitRow) : null,
        featured: Boolean(v.destacado),
        published: true,
        updatedAt: v.fecha_actualizacion || new Date().toISOString(),
        source: 'planilla',
      };
    });

  // Filas con sólo id + fotos: agregan fotos a una unidad existente sin reemplazar sus datos.
  const fullIds = new Set(vehicles.map((v) => v.id));
  const photoOnly: Record<string, string[]> = {};
  raw.vehicles.forEach((v) => {
    if (!v.id || fullIds.has(String(v.id))) return;
    const list = normalizeImageList(v.fotos ?? []);
    if (list.length) photoOnly[String(v.id).trim()] = list;
  });

  return { brands, vehicles, photoOnly };
}

/** Garantiza al menos una foto por unidad. */
export function fallbackImages(): string[] {
  return [images.hero];
}
